<template>
  <div :class="['w-full', { hidden: !isSidebarOpen }]">
    <TransitionGroup
      tag="div"
      class="flex flex-col gap-2"
      enter-active-class="transition-all duration-300 ease-in-out"
      leave-active-class="transition-all duration-300 ease-in-out absolute w-[calc(100%-1rem)]"
      enter-from-class="opacity-0 -translate-y-5"
      leave-to-class="opacity-0 -translate-y-5"
      move-class="transition-all duration-300 ease-in-out"
    >
      <div
        v-for="conversation in conversationsStore.sortedConversations"
        :key="conversation.id"
        class="group relative"
      >
        <router-link
          :to="`/chat/${conversation.id}`"
          :class="[
            'flex flex-col py-[0.45rem] pl-3 pr-16 rounded-lg border border-transparent transition-colors duration-200',
            isActive(conversation.id)
              ? 'bg-nera-elevated-4 text-nera-accent'
              : 'text-nera-text hover:bg-nera-light-hover'
          ]"
        >
          <div class="font-medium text-[0.9rem] truncate">{{ conversation.title }}</div>
          <div class="text-xs opacity-70 mt-1">{{ formatTime(conversation.updated_at) }}</div>
        </router-link>

        <div class="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          <button
            @click.prevent="handleEdit(conversation)"
            :disabled="deletingIds.has(conversation.id)"
            title="Rename"
            aria-label="Rename conversation"
            class="inline-flex items-center justify-center w-7 h-7 rounded-md bg-transparent border-0 text-nera-muted cursor-pointer transition-colors hover:bg-nera-light-hover hover:text-nera-accent disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i class="fas fa-pencil-alt text-[0.7rem]"></i>
          </button>
          <button
            @click.prevent="handleDelete(conversation.id)"
            :disabled="deletingIds.has(conversation.id)"
            title="Delete"
            aria-label="Delete conversation"
            class="inline-flex items-center justify-center w-7 h-7 rounded-md bg-transparent border-0 text-nera-muted cursor-pointer transition-colors hover:bg-nera-danger/10 hover:text-nera-danger disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i class="fas fa-trash-alt text-[0.7rem]"></i>
          </button>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useConversationsStore } from '../stores/conversations'

const route = useRoute()
const router = useRouter()
const conversationsStore = useConversationsStore()
const deletingIds = ref(new Set())

defineProps({
  isSidebarOpen: {
    type: Boolean,
    default: false
  }
})

onMounted(() => {
  conversationsStore.fetchConversations()
})

const isActive = (conversationId) => {
  return route.params.id == conversationId
}

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now - date

  if (diff < 86400000) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }
  if (diff < 604800000) {
    return date.toLocaleDateString('en-US', { weekday: 'short' })
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const handleEdit = (conversation) => {
  if (deletingIds.value.has(conversation.id)) {
    return
  }
  router.push(`/chat/${conversation.id}?edit=title`)
}

const handleDelete = async (conversationId) => {
  if (deletingIds.value.has(conversationId)) {
    return
  }

  if (!confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
    return
  }

  deletingIds.value.add(conversationId)

  try {
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content
    const response = await fetch(`/api/conversations/${conversationId}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      credentials: 'same-origin'
    })

    if (response.ok) {
      const index = conversationsStore.conversations.findIndex(c => c.id === conversationId)
      if (index !== -1) {
        conversationsStore.conversations.splice(index, 1)
      }

      if (route.params.id == conversationId) {
        if (conversationsStore.conversations.length > 0) {
          router.push(`/chat/${conversationsStore.conversations[0].id}`)
        } else {
          router.push('/chat')
        }
      }
    }
  } catch (error) {
    console.error('Failed to delete conversation:', error)
    alert('Failed to delete conversation')
  } finally {
    deletingIds.value.delete(conversationId)
  }
}
</script>
